import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import Properties from '../components/Properties';
import About from '../components/About';
import Services from '../components/Services';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import { getProperties as getPropertiesAPI } from '../services/api';

function Home() {
    const [filteredProperties, setFilteredProperties] = useState([]);
    const [allProperties, setAllProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('home');
    const propertiesRef = useRef(null);

    // Load properties from API on mount
    useEffect(() => {
        const loadProperties = async () => {
            try {
                setLoading(true);
                const properties = await getPropertiesAPI();
                if (Array.isArray(properties)) {
                    setAllProperties(properties);
                    setFilteredProperties(properties);
                } else {
                    setAllProperties([]);
                    setFilteredProperties([]);
                }
            } catch (error) {
                console.error('Error loading properties from API:', error);
                setAllProperties([]);
                setFilteredProperties([]);
            } finally {
                setLoading(false);
            }
        };

        loadProperties();

        // Reload properties when window gains focus (user comes back from admin)
        const handleFocus = async () => {
            try {
                const properties = await getPropertiesAPI();
                if (properties && Array.isArray(properties)) {
                    setAllProperties(properties);
                    setFilteredProperties(properties);
                }
            } catch (error) {
                console.error('Error reloading properties:', error);
            }
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    // Filter properties based on search criteria - now uses API
    const handleSearch = async (searchCriteria) => {
        const { location, type, price } = searchCriteria;
        
        try {
            // Use API to filter properties
            const filtered = await getPropertiesAPI({
                location: location || undefined,
                type: type || undefined,
                price: price || undefined
            });
            
            setFilteredProperties(filtered);

            // Scroll to properties section
            if (propertiesRef.current) {
                const offsetTop = propertiesRef.current.offsetTop - 70;
                window.scrollTo({
                    top: offsetTop,
                    behavior: 'smooth'
                });
            }
        } catch (error) {
            console.error('Error searching properties:', error);
            // Fallback to client-side filtering
            let filtered = allProperties.filter(property => {
                const matchLocation = !location || property.location.toLowerCase().includes(location.toLowerCase());
                const matchType = !type || property.type === type;
                const matchPrice = !price || checkPriceRange(property.price, price);
                return matchLocation && matchType && matchPrice;
            });
            setFilteredProperties(filtered);
        }
    };

    // Check price range
    const checkPriceRange = (price, range) => {
        if (range === '0-500000') {
            return price <= 500000;
        } else if (range === '500000-1000000') {
            return price > 500000 && price <= 1000000;
        } else if (range === '1000000-2000000') {
            return price > 1000000 && price <= 2000000;
        } else if (range === '2000000+') {
            return price > 2000000;
        }
        return true;
    };

    // Handle navigation click
    const handleNavClick = (section) => {
        setActiveSection(section);
        const element = document.getElementById(section);
        if (element) {
            const offsetTop = element.offsetTop - 70;
            window.scrollTo({
                top: offsetTop,
                behavior: 'smooth'
            });
        }
    };

    // Update active section on scroll
    useEffect(() => {
        const handleScroll = () => {
            const sections = ['home', 'properties', 'about', 'services', 'contact'];
            const scrollY = window.pageYOffset;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = document.getElementById(sections[i]);
                if (section) {
                    const sectionTop = section.offsetTop - 100;
                    const sectionHeight = section.offsetHeight;
                    
                    if (scrollY >= sectionTop && scrollY < sectionTop + sectionHeight) {
                        setActiveSection(sections[i]);
                        break;
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="App">
            <Navbar activeSection={activeSection} onNavClick={handleNavClick} />
            <Hero onSearch={handleSearch} />
            <div ref={propertiesRef}>
                <Properties properties={filteredProperties} />
            </div>
            <About />
            <Services />
            <Contact />
            <Footer onNavClick={handleNavClick} />
        </div>
    );
}

export default Home;
